/**
 * EquipmentSystem.ts
 * Manages character equipment and stat calculations
 */

import { Character, ItemType } from '../lib/types/game';
import { getItem, canClassUseItem } from './data/ItemData';

export interface EquipmentSlot {
  weapon: number;
  armor: number;
  helmet: number;
  gloves: number;
  accessory: number;
}

export interface EquipmentStats {
  attack: number;
  defense: number;
  accuracy: number;
  critBonus: number;
  magicDefense: number;
}

export interface EquipResult {
  success: boolean;
  message: string;
  unequippedItem?: number; // Item ID that was unequipped (if any)
}

/**
 * Equipment management system
 * Handles equipping/unequipping items and calculating stat bonuses
 */
export class EquipmentSystem {
  /**
   * Check if a character can equip an item
   */
  static canEquip(character: Character, itemId: number): { canEquip: boolean; reason?: string } {
    if (itemId === 0) {
      return { canEquip: true }; // Can always unequip
    }

    const item = getItem(itemId);

    if (!item) {
      return { canEquip: false, reason: 'Item not found' };
    }

    // Check if item is equipment
    if (item.type === ItemType.CONSUMABLE || item.type === ItemType.KEY_ITEM) {
      return { canEquip: false, reason: 'Cannot equip consumables' };
    }

    // Check class restrictions
    if (!canClassUseItem(itemId, character.class)) {
      return { canEquip: false, reason: 'Class cannot use this item' };
    }

    return { canEquip: true };
  }

  /**
   * Equip an item to a character
   * Returns the item ID that was unequipped (if any)
   */
  static equipItem(character: Character, itemId: number): EquipResult {
    if (itemId === 0) {
      return { success: false, message: 'Invalid item' };
    }

    const item = getItem(itemId);
    if (!item) {
      return { success: false, message: 'Item not found' };
    }

    // Check if can equip
    const { canEquip, reason } = this.canEquip(character, itemId);
    if (!canEquip) {
      return { success: false, message: reason || 'Cannot equip' };
    }

    let unequippedItem: number | undefined;

    // Equip to appropriate slot
    switch (item.type) {
      case ItemType.WEAPON:
        unequippedItem = character.weapon || undefined;
        character.weapon = itemId;
        break;

      case ItemType.ARMOR:
        unequippedItem = character.armor || undefined;
        character.armor = itemId;
        break;

      case ItemType.HELMET:
        unequippedItem = character.helmet || undefined;
        character.helmet = itemId;
        break;

      case ItemType.GLOVES:
        unequippedItem = character.gloves || undefined;
        character.gloves = itemId;
        break;

      case ItemType.ACCESSORY:
        unequippedItem = character.accessory || undefined;
        character.accessory = itemId;
        break;

      default:
        return { success: false, message: 'Cannot equip this item type' };
    }

    return {
      success: true,
      message: `Equipped ${item.name}`,
      unequippedItem,
    };
  }

  /**
   * Unequip an item from a character
   */
  static unequipItem(character: Character, slot: ItemType): EquipResult {
    let unequippedItem = 0;
    let slotName = '';

    switch (slot) {
      case ItemType.WEAPON:
        unequippedItem = character.weapon;
        character.weapon = 0;
        slotName = 'weapon';
        break;

      case ItemType.ARMOR:
        unequippedItem = character.armor;
        character.armor = 0;
        slotName = 'armor';
        break;

      case ItemType.HELMET:
        unequippedItem = character.helmet;
        character.helmet = 0;
        slotName = 'helmet';
        break;

      case ItemType.GLOVES:
        unequippedItem = character.gloves;
        character.gloves = 0;
        slotName = 'gloves';
        break;

      case ItemType.ACCESSORY:
        unequippedItem = character.accessory;
        character.accessory = 0;
        slotName = 'accessory';
        break;

      default:
        return { success: false, message: 'Invalid equipment slot' };
    }

    if (unequippedItem === 0) {
      return { success: false, message: 'Nothing equipped in that slot' };
    }

    const item = getItem(unequippedItem);
    return {
      success: true,
      message: `Unequipped ${item?.name || 'item'} from ${slotName}`,
      unequippedItem,
    };
  }

  /**
   * Get all equipped items for a character
   */
  static getEquippedItems(character: Character): EquipmentSlot {
    return {
      weapon: character.weapon,
      armor: character.armor,
      helmet: character.helmet,
      gloves: character.gloves,
      accessory: character.accessory,
    };
  }

  /**
   * Calculate equipment stat bonuses
   */
  static calculateEquipmentStats(character: Character): EquipmentStats {
    const stats: EquipmentStats = {
      attack: 0,
      defense: 0,
      accuracy: 0,
      critBonus: 0,
      magicDefense: 0,
    };

    const equipment = [
      character.weapon,
      character.armor,
      character.helmet,
      character.gloves,
      character.accessory,
    ];

    for (const itemId of equipment) {
      if (itemId === 0) continue;

      const item = getItem(itemId);
      if (!item) continue;

      // Weapons contribute to attack
      if (item.type === ItemType.WEAPON) {
        stats.attack += item.power;
        stats.accuracy += item.accuracy;
        stats.critBonus += item.critBonus;
      }

      // Armor/helmet/gloves/accessories contribute to defense
      if (item.type === ItemType.ARMOR ||
          item.type === ItemType.HELMET ||
          item.type === ItemType.GLOVES ||
          item.type === ItemType.ACCESSORY) {
        stats.defense += item.power;
        stats.accuracy += item.accuracy;
        stats.critBonus += item.critBonus;

        // Accessories and robes can provide magic defense
        if (item.type === ItemType.ACCESSORY || item.type === ItemType.ARMOR) {
          stats.magicDefense += Math.floor(item.power / 2);
        }
      }
    }

    return stats;
  }

  /**
   * Calculate total attack power (base STR + equipment)
   */
  static calculateAttackPower(character: Character): number {
    const equipStats = this.calculateEquipmentStats(character);
    return character.strength + equipStats.attack;
  }

  /**
   * Calculate total defense (base VIT + equipment)
   */
  static calculateDefense(character: Character): number {
    const equipStats = this.calculateEquipmentStats(character);
    return character.vitality + equipStats.defense;
  }

  /**
   * Calculate total accuracy
   */
  static calculateAccuracy(character: Character): number {
    const equipStats = this.calculateEquipmentStats(character);
    return character.agility + equipStats.accuracy;
  }

  /**
   * Calculate total critical hit bonus
   */
  static calculateCritBonus(character: Character): number {
    const equipStats = this.calculateEquipmentStats(character);
    return character.luck + equipStats.critBonus;
  }

  /**
   * Calculate magic defense
   */
  static calculateMagicDefense(character: Character): number {
    const equipStats = this.calculateEquipmentStats(character);
    return character.intelligence + equipStats.magicDefense;
  }

  /**
   * Get optimal equipment from inventory for a character
   * Returns list of item IDs that provide best stats
   */
  static getOptimalEquipment(
    character: Character,
    availableItems: number[]
  ): Partial<EquipmentSlot> {
    const optimal: Partial<EquipmentSlot> = {};

    const slots = [
      ItemType.WEAPON,
      ItemType.ARMOR,
      ItemType.HELMET,
      ItemType.GLOVES,
      ItemType.ACCESSORY,
    ];

    for (const slot of slots) {
      let bestItem = 0;
      let bestPower = 0;

      for (const itemId of availableItems) {
        const item = getItem(itemId);
        if (!item || item.type !== slot) continue;

        const { canEquip } = this.canEquip(character, itemId);
        if (!canEquip) continue;

        // Compare power (for weapons) or defense (for armor)
        const itemPower = item.power;
        if (itemPower > bestPower) {
          bestPower = itemPower;
          bestItem = itemId;
        }
      }

      if (bestItem > 0) {
        switch (slot) {
          case ItemType.WEAPON:
            optimal.weapon = bestItem;
            break;
          case ItemType.ARMOR:
            optimal.armor = bestItem;
            break;
          case ItemType.HELMET:
            optimal.helmet = bestItem;
            break;
          case ItemType.GLOVES:
            optimal.gloves = bestItem;
            break;
          case ItemType.ACCESSORY:
            optimal.accessory = bestItem;
            break;
        }
      }
    }

    return optimal;
  }

  /**
   * Check if item is currently equipped by character
   */
  static isEquipped(character: Character, itemId: number): boolean {
    return (
      character.weapon === itemId ||
      character.armor === itemId ||
      character.helmet === itemId ||
      character.gloves === itemId ||
      character.accessory === itemId
    );
  }

  /**
   * Get equipment slot that an item is equipped in
   */
  static getEquippedSlot(character: Character, itemId: number): ItemType | null {
    if (character.weapon === itemId) return ItemType.WEAPON;
    if (character.armor === itemId) return ItemType.ARMOR;
    if (character.helmet === itemId) return ItemType.HELMET;
    if (character.gloves === itemId) return ItemType.GLOVES;
    if (character.accessory === itemId) return ItemType.ACCESSORY;
    return null;
  }

  /**
   * Get equipment comparison (current vs new item)
   */
  static compareEquipment(
    character: Character,
    newItemId: number
  ): {
    valid: boolean;
    currentPower: number;
    newPower: number;
    difference: number;
  } {
    const newItem = getItem(newItemId);
    if (!newItem) {
      return { valid: false, currentPower: 0, newPower: 0, difference: 0 };
    }

    let currentItemId = 0;

    switch (newItem.type) {
      case ItemType.WEAPON:
        currentItemId = character.weapon;
        break;
      case ItemType.ARMOR:
        currentItemId = character.armor;
        break;
      case ItemType.HELMET:
        currentItemId = character.helmet;
        break;
      case ItemType.GLOVES:
        currentItemId = character.gloves;
        break;
      case ItemType.ACCESSORY:
        currentItemId = character.accessory;
        break;
      default:
        return { valid: false, currentPower: 0, newPower: 0, difference: 0 };
    }

    const currentItem = getItem(currentItemId);
    const currentPower = currentItem?.power || 0;
    const newPower = newItem.power;
    const difference = newPower - currentPower;

    return {
      valid: true,
      currentPower,
      newPower,
      difference,
    };
  }

  /**
   * Debug: Print character equipment
   */
  static debug(character: Character): void {
    console.log('=== EQUIPMENT ===');
    const equipment = this.getEquippedItems(character);

    const slots = [
      { name: 'WEAPON', id: equipment.weapon },
      { name: 'ARMOR', id: equipment.armor },
      { name: 'HELMET', id: equipment.helmet },
      { name: 'GLOVES', id: equipment.gloves },
      { name: 'ACCESSORY', id: equipment.accessory },
    ];

    slots.forEach(({ name, id }) => {
      const item = getItem(id);
      console.log(`${name}: ${item?.name || 'NONE'}`);
    });

    const stats = this.calculateEquipmentStats(character);
    console.log('=== STATS ===');
    console.log(`Attack: ${stats.attack}`);
    console.log(`Defense: ${stats.defense}`);
    console.log(`Accuracy: ${stats.accuracy}`);
    console.log(`Crit Bonus: ${stats.critBonus}`);
    console.log(`Magic Defense: ${stats.magicDefense}`);
  }
}
